import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Eye, Palette, Layout, Monitor, Camera, MessageCircle } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

const Highlight = ({ children }) => (
  <span className="relative inline-block font-accent">
    <span className="absolute left-0 right-0 bottom-[0.08em] h-[0.8em] bg-[#BE123C] -z-0" />
    <span className="relative z-10">{children}</span>
  </span>
);

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function VisualIdentityPage() {
  return (
    <main data-testid="visual-identity-page">

      {/* 1. HERO */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/grey-waves.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/80 lg:bg-transparent" />
          <div className="hidden lg:block absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(28,18,16,0.88) 40%, rgba(28,18,16,0.5) 65%, rgba(28,18,16,0.3) 100%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">THE FULL EXPERIENCE</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">FULL BRAND SYSTEM</span>
              <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">Stop Overexplaining Your Value. Start Showing It.</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-10">
              Your business is already good. Your clients already know it. The problem is that everyone else has to figure it out for themselves. This program makes your brand do the talking so you can stop repeating yourself. Everything from <Link to="/brand-foundation" className="text-white/80 underline hover:text-white">Brand Foundation</Link> plus a complete visual identity built to last.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              {["Everything in Brand Foundation", "Full Visual Identity System", "Templates and assets", "1 month rollout support"].map((label, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 px-4 py-2">
                  <Check size={12} className="text-[#D4A0A0]" />
                  <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                </div>
              ))}
            </div>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
              BOOK A CLARITY CALL <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. THE REAL PROBLEM */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE REAL PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">YOUR BUSINESS IS GOOD</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">but your brand does not show it yet</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>You know the feeling. Every time someone asks for your link, there is a quiet flinch. The website looks okay but it does not carry your level. Your Instagram feed looks like a mixed moodboard instead of a clear brand. You keep tweaking, redesigning and rewriting, and nothing holds.</p>
                <p>The issue is not that you need a better logo. Your visuals, your message and your positioning are not connected to the same truth. People hesitate because they feel something is off. That is what we fix.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-10">
              {[
                { title: "People visit your site and leave", body: "The visuals look fine but nothing signals why they should stay or trust you." },
                { title: "Your feed looks like five different businesses", body: "Every post has a different font, filter or energy. There is no system holding it together." },
                { title: "You keep redesigning instead of selling", body: "Every few months you redo the logo, the colors, the layout. The brand never settles because the foundation underneath is missing." },
              ].map((card, i) => (
                <div key={i} className="border-l-2 border-[#BE123C] pl-6">
                  <h3 className="font-body text-base font-semibold text-[#1C1210] mb-1.5">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. WHAT THIS INCLUDES */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHAT THIS INCLUDES</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">TWO LAYERS OF WORK</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">strategy first, then visual expression</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-white border border-[#e5e2de] p-8 md:p-10">
              <span className="font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-4 block">LAYER ONE</span>
              <h3 className="font-display text-2xl text-[#1C1210] mb-2">Brand Foundation</h3>
              <p className="font-accent text-base text-[#BE123C] mb-4">Who you are and what your brand stands for</p>
              <p className="font-body text-sm text-[#9A9590] mb-6">Everything included in <Link to="/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link></p>
              <ul className="space-y-3">
                {[
                  "Brand audit of your current presence",
                  "Values, vision and positioning",
                  "Ideal customer definition",
                  "Brand identity interview",
                  "Messaging and tone of voice",
                  "USP and competitive clarity",
                  "Brand Foundation Book",
                  "DNA Voice Agent (your AI writing partner)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="bg-white border border-[#e5e2de] p-8 md:p-10">
              <span className="font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-4 block">LAYER TWO</span>
              <h3 className="font-display text-2xl text-[#1C1210] mb-2">Visual Identity System</h3>
              <p className="font-accent text-base text-[#BE123C] mb-6">How your brand looks and feels everywhere</p>
              <ul className="space-y-3">
                {[
                  "Three logo directions (you choose one)",
                  "Full design system: typography, colors, patterns, layout logic",
                  "Logo and brand mark",
                  "Social media templates (posts, stories, highlights)",
                  "Business documents (invoices, proposals, presentations)",
                  "Brand guidelines document",
                  "Canva or Figma templates for your team",
                  "Curated image library (30 brand photos + 30 stock images)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">THE PROCESS</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FROM FIRST CALL</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">to a brand that runs itself</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery and Foundation", body: "We start with the Brand Foundation work: interviews, workshops, positioning. You walk away with your Brand Foundation Book and DNA Voice Agent.", weeks: "Weeks 1 to 3" },
              { step: "02", title: "Three visual directions", body: "I create three distinct creative directions for your visual identity. Each one is grounded in the foundation work. You choose the one that feels right.", weeks: "Week 4" },
              { step: "03", title: "Full system build", body: "I develop your chosen direction into a complete system: logo, typography, colors, layout logic, templates, social media kit, business documents and brand guidelines.", weeks: "Weeks 5 to 7" },
              { step: "04", title: "Rollout support", body: "After delivery, I stay available for one month via WhatsApp, Monday to Friday during working hours. I help you apply the new identity and answer questions as they come up.", weeks: "Month 3" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="border-t border-[#1C1210]/15 pt-6">
                <span className="font-display text-4xl text-[#BE123C] block mb-4 leading-none">{s.step}</span>
                <h3 className="font-serif text-lg text-[#1C1210] mb-2">{s.title}</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7] mb-4">{s.body}</p>
                <span className="font-body text-xs text-[#9A9590] uppercase tracking-[1px]">{s.weeks}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. TESTIMONIAL (white card on Apple grey, BF page convention) */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">THE PROOF</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WHAT FOUNDERS SAY</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">after the full system is in place</span>
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="bg-white p-8 md:p-12 relative border border-[#e5e2de]">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Wcześniej potrafiłam siedzieć godzinami nad grafiką i efekt i tak nie był taki, jak chciałam. Był nawet moment, że wydawało mi się, że już jest dobrze, a potem zobaczyłam materiały przygotowane przez Olę i zobaczyłam, jaka jest różnica.</p>
                <p>Teraz mam gotowe szablony. Podmieniam zdjęcie i tekst i wszystko jest spójne, estetyczne i wygląda profesjonalnie. To oszczędza mi bardzo dużo czasu i daje pewność, że to po prostu dobrze wygląda.</p>
                <p>Nauczyłam się tego, czego wcześniej nie wiedziałam, czyli jak pisać treści, w jakim stylu je tworzyć i co właściwie chcemy komunikować klientowi. W końcu wiem, czym jest marka Galikowski Finanse i jaka ma być dla klienta.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Galikowski Finanse, Full Brand System client</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. DIVIDER */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-snug">
              When your brand is inconsistent, people hesitate. They cannot always name why. They just feel: something is off. This program removes that feeling by aligning your truth with your visuals and your message. That is why trust becomes <Highlight>faster.</Highlight> That is why buying becomes <Highlight>easier.</Highlight>
            </p>
          </motion.div>
        </div>
      </section>

      {/* 7. WHAT CHANGES (based on real testimonials) */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">AFTER THE WORK IS DONE</span>
            <h2 className="mb-12">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WHAT ACTUALLY CHANGES</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">in your day-to-day</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { Icon: Eye, text: "You send your link without the stomach drop. Your website, Instagram and offer pages carry your level. You stop hesitating before sharing because it finally looks and sounds like you." },
              { Icon: Palette, text: "You stop sitting for hours over a single graphic. With ready templates, you swap a photo and a line of text and everything comes out consistent and professional. That time goes back to your clients." },
              { Icon: Monitor, text: "People trust you faster. A stranger lands on your page and immediately gets what you do, who it is for and what kind of experience they can expect. No more explaining yourself in every conversation." },
              { Icon: Layout, text: "You know what to say and how to say it. Before, you wrote content on instinct. Now you have a clear direction for your messaging, your tone and what you want to communicate. The guessing stops." },
              { Icon: Camera, text: "You see the difference between what you had before and what you have now. One of my clients told me she thought her old brand was fine, until she saw the new materials and realized how big the gap was." },
              { Icon: MessageCircle, text: "You understand your own brand for the first time. You can explain who you are and why it matters without fumbling. Pricing conversations get easier because your perceived value finally matches the real one." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4 bg-[#f5f5f7] p-6 border border-[#e5e2de]">
                <item.Icon size={20} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHO IS THIS FOR */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">WHO THIS IS FOR</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FOR FOUNDERS WHO ARE DONE</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">with "almost right" branding</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7]">
                <p>You have real results, real clients and real depth. Your business is not the problem. The way it comes across is. You have tried Canva, you have tried freelancers, you may have tried doing it yourself. Every time, the result looks okay but does not feel like you.</p>
                <p>This program is for the moment when you are ready to stop patching and start building a brand that holds up everywhere: on your website, on Instagram, on a business card, on a slide deck, in a DM.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center">
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Start here if</span> you want a brand system that works across every touchpoint, built from a real strategic foundation, with visual identity designed to last.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  Not the right fit if you are looking for a quick logo refresh or a social media template pack without strategic work behind it. For strategy only, see <Link to="/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link>.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  "Founders and small teams (up to ~50 employees)",
                  "Businesses with real traction but a brand that has not kept up",
                  "Anyone going through a pivot, rebrand or significant growth moment",
                  "People who want personal, hands-on work with a creative director",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                    <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 9. WHO IS BEHIND THIS */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/mood/flower-hand.jpg" alt="KALZA Studio" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHO IS BEHIND THIS</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">ALEKSANDRA KAŁUŻA</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">KALZA Studio</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#4A4540] leading-[1.7]">
                <p>I spent years inside global brand teams, leading creative direction at Just Eat Takeaway and running campaigns for Heineken, Samsung and UEFA Champions League. My job was to make sure every touchpoint carries the same truth. I still do that, but now for founders building their own businesses.</p>
                <p>I go inside the business, audit the internal and external reality and lead decisions through a controlled process. The output is not a logo pack that dies in a folder. It is a system that runs the business: templates, assets, guidelines and a design logic that prevents messy DIY drift.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 10. THE INVESTMENT */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE INVESTMENT</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">PRICED FOR WHERE</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">your business is right now</span>
            </h2>
            <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-2xl mb-12">
              <p>Full Brand System is a five-figure investment. The exact number depends on the scale of your business, because a solo founder and a company with a full team require different depth. I set the price after we talk about your situation so it reflects what your business actually needs.</p>
              <p>A new identity is a big decision and should not be rushed. The timeline of around two months is intentional: it includes time for research, creation, reflection, revisions and proper rollout planning.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                { label: "Solo founder", desc: "One person, clear core offer, ready for a complete brand system that holds" },
                { label: "Small team", desc: "A few employees, multiple offers, the brand needs to work across the whole business" },
                { label: "Growing company", desc: "Larger team, complex structure, brand alignment across departments and client touchpoints" },
              ].map((tier, i) => (
                <div key={i} className="bg-[#f5f5f7] p-6 border border-[#e5e2de]">
                  <h4 className="font-serif text-base text-[#1C1210] mb-2">{tier.label}</h4>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{tier.desc}</p>
                </div>
              ))}
            </div>
            <p className="font-body text-sm text-[#9A9590] mb-10">Every engagement includes the full Brand Foundation, complete Visual Identity System, templates, DNA Voice Agent and one month of rollout support via WhatsApp.</p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
              LET'S DISCUSS YOUR INVESTMENT <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">READY WHEN YOU ARE</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">BUILD A BRAND THAT SPEAKS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">so you can stop explaining</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              If your business deserves better than what your brand is currently showing, this is the work that closes that gap. Book a call and I will tell you exactly what I see.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              BOOK A CLARITY CALL <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
